import {makeSyncStoreUtil} from '@features/utils/syncStoreUtil';
import {GenerationScreenState} from './GenerationStreamState';
import type {GenType} from '@features/flow/flowService';
import type {SessionLimitInfo} from '@features/subscription/sessionLimitAtom';

export class GenerationManager {
  screens = new Map<string, GenerationScreenState>();
  screenListStore = makeSyncStoreUtil();
  allScreenStatuses = makeSyncStoreUtil(this.screenListStore);

  setScreenType: (screenId: string, genType: GenType | null) => void = () => {};

  fetchScreenContents: (screenId: string) => Promise<string> = () =>
    Promise.resolve('');

  sendAbortGeneration: (abortToken: string) => void = () => {};

  onSessionLimit: (info: SessionLimitInfo) => void = () => {};

  invalidate: (
    flowId: string,
    screenId: string,
    messageId: string,
  ) => Promise<void> = () => Promise.resolve();

  getInitialScreenIds: (flowId: string) => Set<string> = () => new Set();

  logScreenEvents: (
    flowId: string,
    initialScreenIds: Set<string>,
    touchedScreenIds: Set<string>,
  ) => void = () => {};

  getScreen(
    flowId: string,
    screenId: string,
    triggerGeneration = true,
  ): GenerationScreenState {
    const existing = this.screens.get(screenId);
    if (existing) return existing;

    const newScreen = new GenerationScreenState(flowId, screenId, this);
    this.screens.set(screenId, newScreen);
    if (triggerGeneration) {
      setTimeout(() => this.resumeSequentially(flowId, screenId), 50);
    }

    setTimeout(() => this.screenListStore.trigger(), 1);

    return newScreen;
  }

  #flowResumeQueues = new Map<string, string[]>();

  /**
   * Auto-resumes (screen mount + session-reset timers) run one screen at a
   * time per flow: components screens share one filesystem, so parallel turns
   * would race each other and lose work (last-writer-wins on the snapshot
   * chain). While any generation runs on the flow, further resumes queue and
   * start from #cleanupWhenDone as each turn settles.
   */
  resumeSequentially(flowId: string, screenId: string) {
    const busy = [...this.screens.values()].some(
      (s) => s.flowId === flowId && s.generationPromise !== null,
    );
    const queue = this.#flowResumeQueues.get(flowId) ?? [];
    if (busy || queue.length > 0) {
      if (!queue.includes(screenId)) queue.push(screenId);
      this.#flowResumeQueues.set(flowId, queue);
      return;
    }
    this.screens.get(screenId)?.triggerDownload('auto');
  }

  startNextResume(flowId: string) {
    const next = this.#flowResumeQueues.get(flowId)?.shift();
    if (next === undefined) return;
    this.screens.get(next)?.triggerDownload('auto');
  }
}
